import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Grid, Chip, CircularProgress, Alert, TextField, 
    Avatar, Stack, useTheme 
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import 'moment/locale/es';
import { api } from '../../controllers/ConsumeApiController'; 
// Importa iconos si quieres ponerle uno a los Activos (Canchas) que no tienen foto
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuildingUser } from '@fortawesome/free-solid-svg-icons';
import publicRoutes from '../../apiRoutes/publicRoutes';

moment.locale('es');

export default function BookingWidget({ productId, onSlotSelect }) {
    const theme = useTheme();

    // --- ESTADOS ---
    const [selectedDate, setSelectedDate] = useState(null);
    const [slots, setSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    
    // Recursos
    const [resources, setResources] = useState([]); 
    // Ahora selectedResource guarda el OBJETO completo {id, type, name...}
    const [selectedResource, setSelectedResource] = useState(null); 
    const [isLoadingResources, setIsLoadingResources] = useState(false);

    const [loadingSlots, setLoadingSlots] = useState(false);
    const [error, setError] = useState('');

    // 1. CARGAR RECURSOS
    useEffect(() => {
        const fetchResources = async () => {
            setIsLoadingResources(true);
            try {
                const url = publicRoutes.booking.availabilityResource(productId);
                const res = await api.get(url);
                if (res.success && res.data.length > 0) {
                    setResources(res.data);
                }
            } catch (err) {
                console.error("Error recursos:", err);
            }
            setIsLoadingResources(false);
        };
        if (productId) fetchResources();
    }, [productId]);


    // 2. BUSCAR HORARIOS
    useEffect(() => {
        if (selectedDate) {
            // Obligatorio seleccionar recurso si existen opciones
            if (resources.length > 0 && !selectedResource) {
                setSlots([]);
                return; 
            }
            fetchSlots();
        } else {
            setSlots([]);
        }
    }, [selectedDate, selectedResource]); 


    const fetchSlots = async () => {
        setLoadingSlots(true);
        setError('');
        setSlots([]);
        setSelectedSlot(null);
        onSlotSelect(null);

        try {
            const dateStr = selectedDate.format('YYYY-MM-DD');
            let url = publicRoutes.booking.availability+`/${productId}?date=${dateStr}`;
            
            // --- FIX DE LA URL: ENVIAMOS ID Y TYPE ---
            if (selectedResource) {
                url += `&resource_id=${selectedResource.id}&resource_type=${selectedResource.type}`;
            }

            const res = await api.get(url);
            
            if (res.success) {
                setSlots(res.data);
            } else {
                setError(res.data.message || 'No hay disponibilidad.');
            }
        } catch (err) {
            console.error(err);
            setError('Error obteniendo horarios.');
        }
        setLoadingSlots(false);
    };

    const handleSlotClick = (slot) => {
        setSelectedSlot(slot);
        onSlotSelect({
            date: selectedDate.toDate(), 
            time: slot.time,    
            displayTime: slot.display,
            resource: selectedResource
        });
    };

    return (
        <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="es">
            <Box sx={{ mt: 3 }}>
                
                {/* --- SECCIÓN 1: SELECTOR DE PROFESIONAL (ESTILO AVATAR) --- */}
                {resources.length > 0 && (
                    <Box mb={3}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Selecciona Profesional / Espacio:
                        </Typography>
                        
                        {isLoadingResources ? (
                             <CircularProgress size={20} />
                        ) : (
                            <Box 
                                sx={{ 
                                    display: 'flex', 
                                    gap: 2, 
                                    overflowX: 'auto', 
                                    pb: 1,
                                    '::-webkit-scrollbar': { display: 'none' } // Ocultar scrollbar visualmente
                                }}
                            >
                                {/* Opción "Cualquiera" (Opcional) */}
                                {/* <Box 
                                    onClick={() => setSelectedResource(null)}
                                    sx={{ 
                                        cursor: 'pointer', 
                                        textAlign: 'center', 
                                        opacity: selectedResource === null ? 1 : 0.5 
                                    }}
                                >
                                    <Avatar sx={{ width: 56, height: 56, bgcolor: 'grey.300', margin: '0 auto' }}>
                                        ?
                                    </Avatar>
                                    <Typography variant="caption" mt={1}>Cualquiera</Typography>
                                </Box> 
                                */}

                                {resources.map((res) => {
                                    const isSelected = selectedResource?.id === res.id && selectedResource?.type === res.type;
                                    
                                    return (
                                        <Box 
                                            key={`${res.type}-${res.id}`}
                                            onClick={() => setSelectedResource(res)}
                                            sx={{ 
                                                cursor: 'pointer', 
                                                textAlign: 'center',
                                                minWidth: 70,
                                                opacity: isSelected ? 1 : 0.6,
                                                transition: '0.2s',
                                                '&:hover': { opacity: 1 }
                                            }}
                                        >
                                            {/* CONTENEDOR DEL AVATAR CON BORDE ACTIVO */}
                                            <Box sx={{ 
                                                display: 'inline-block',
                                                p: 0.3, // Espacio para el borde
                                                borderRadius: '50%',
                                                border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent'
                                            }}>
                                                <Avatar 
                                                    src={res.image} 
                                                    alt={res.name}
                                                    sx={{ width: 56, height: 56, bgcolor: 'primary.light' }}
                                                >
                                                    {/* Fallback si no hay imagen: Inicial o Icono */}
                                                    {!res.image && (
                                                        res.type === 'employee' 
                                                            ? res.name.charAt(0) 
                                                            : <FontAwesomeIcon icon={faBuildingUser} />
                                                    )}
                                                </Avatar>
                                            </Box>
                                            
                                            <Typography 
                                                variant="caption" 
                                                display="block" 
                                                sx={{ 
                                                    fontWeight: isSelected ? 'bold' : 'normal',
                                                    mt: 0.5,
                                                    lineHeight: 1.2
                                                }}
                                            >
                                                {res.name.split(' ')[0]} {/* Solo primer nombre para que no se rompa */}
                                            </Typography>
                                            
                                            {/* Aquí irían las estrellitas a futuro */}
                                            {/* <Typography variant="caption" color="gold">★ 4.8</Typography> */}
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                )}

                {/* --- SECCIÓN 2: CALENDARIO Y HORAS --- */}
                {/* Solo mostramos esto si ya seleccionó recurso (o si no hay recursos requeridos) */}
                { (resources.length === 0 || selectedResource) && (
                    <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 2, bgcolor: '#fafafa' }}>
                        
                        <Stack spacing={2}>
                            <DatePicker
                                label="Selecciona la fecha"
                                value={selectedDate}
                                onChange={(newValue) => setSelectedDate(newValue)}
                                disablePast
                                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                            />
                        </Stack>

                        <Box mt={3}>
                            {loadingSlots ? (
                                <Box display="flex" justifyContent="center"><CircularProgress size={24} /></Box>
                            ) : error ? (
                                <Alert severity="warning" sx={{ fontSize: '0.85rem' }}>{error}</Alert>
                            ) : slots.length > 0 ? (
                                <>
                                    <Typography variant="caption" color="text.secondary" mb={1} display="block">
                                        Horarios disponibles:
                                    </Typography>
                                    <Grid container spacing={1}>
                                        {slots.map((slot, index) => (
                                            <Grid item key={index}>
                                                <Chip
                                                    label={slot.display}
                                                    onClick={() => handleSlotClick(slot)}
                                                    color={selectedSlot?.time === slot.time ? "primary" : "default"}
                                                    variant={selectedSlot?.time === slot.time ? "filled" : "outlined"}
                                                    clickable
                                                    sx={{ fontWeight: 'bold' }}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </>
                            ) : selectedDate ? (
                                <Typography variant="caption" color="text.secondary">
                                    No hay disponibilidad para esta fecha.
                                </Typography>
                            ) : (
                                <Typography variant="caption" color="text.secondary">
                                    Elige una fecha para ver horarios.
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        </LocalizationProvider>
    );
}